import Lead from 'classes/Lead.js';
import User from 'classes/User.js';

import { CustomerFeedbackResponses, AppointmentsCalendar, AppointmentsList, FeedbackTemplates } from 'managers/Appointments.js';
import { OutboundCallsCalendar, OutboundCallLogs, Leads, LeadImportHistory, LeadLocations } from 'managers/Leads.js';
import { Clients, ClientLocationsMap, EventTypesList, LeadScriptsList, LeadSubTypesList, LeadTypesList, StatusCodesList } from 'managers/Clients.js';
import { Notifications, SystemEvents, Users, UserAvailabilityPanel } from 'managers/Users.js';
import { Surveys, SurveyTemplates } from 'managers/Surveys.js';

export const getContent = utils => {
    try {

        // declare user and prepare general layout
        let user = utils.user.get();
        let dashboard = [{
            key: 'appointments_calendar',
            Component: AppointmentsCalendar
        },{
            key: 'appointments',
            Component: AppointmentsList.bind(this, {
                cache_key: 'general'
            })
        },{
            key: 'leads',
            title: 'Leads',
            Component: Leads.bind(this, {
                id: 'leads',
                name: 'Leads',
                props: {
                    cache_key: 'general',
                    manage_duplicates: true
                }
            })
        }]

        // determine which type of user account is requesting panel content
        switch(user.level) {
            case User.levels.get().setter:
            dashboard = [{
                key: 'appointments_calendar',
                Component: AppointmentsCalendar
            },{
                key: 'appointments',
                Component: AppointmentsList.bind(this, {
                    cache_key: 'general'
                })
            },{
                key: 'assigned_leads',
                permissions: ['leads.list.assigned'],
                Component: Leads.bind(this, {
                    id: 'assigned_leads',
                    name: 'Assigned Leads',
                    props: {
                        cache_key: 'assigned',
                        assignments: true
                    }
                })
            },{
                key: 'leads',
                Component: Leads.bind(this, {
                    id: 'leads',
                    name: 'Leads',
                    permissions: ['leads.list.general'],
                    props: {
                        cache_key: 'general_marketing',
                        exclude_status_codes: [ Lead.status.get().assigned ],
                        manage_duplicates: true
                    }
                })
            },{
                key: 'lead_scripts_list',
                permissions: ['leads.list.scripts'],
                Component: LeadScriptsList
            }];
            break;
        }

        // create user lists and allow visibility for levels with less access than current user
        let levels = [{
            key: 'admin',
            level: User.levels.get().admin,
            title: 'Administrators',
            shortName: 'Admin'
        },{
            key: 'sales_lead',
            level: User.levels.get().sales_lead,
            title: 'Sales Leads',
            shortName: 'Sales Lead',
            permissions: ['users.list.sales_leads'],
        },{
            key: 'primary',
            level: User.levels.get().primary,
            title: 'Primary Users',
            shortName: 'Primary User'
        },{
            key: 'setter',
            level: User.levels.get().setter,
            title: 'Setters',
            shortName: 'Setter',
            permissions: ['users.list.setters'],
        },{
            key: 'sales_ops',
            level: User.levels.get().sales_ops,
            title: 'Sales Operatives',
            shortName: 'Sales Operative',
            permissions: ['users.list.sales_ops'],
        }];
        
        return {
            dashboard: {
                key: 'dashboard',
                title: 'Dashboard',
                icon: 'user-navigation-dashboard',
                panels: dashboard
            },
            appointments: {
                key: 'appointments',
                title: 'Appointments',
                icon: 'user-navigation-appointments',
                subViews: {
                    browse: {
                        key: 'browse',
                        title: 'Browse',
                        panels: [{
                            key: 'appointments',
                            permissions: ['appointments.list.all'],
                            Component: AppointmentsList.bind(this, {
                                cache_key: 'general'
                            })
                        }]
                    },
                    feedback: {
                        key: 'feedback',
                        title: 'Feedback',
                        panels: [{
                            key: 'customer_feedback_responses',
                            permissions: ['appointments.list.customer_responses'],
                            Component: CustomerFeedbackResponses
                        },{
                            key: 'feedback_templates',
                            Component: FeedbackTemplates,
                            visible: utils.user.get().level <= User.levels.get().primary
                        }]
                    }
                }
            },
            ...user.level <= User.levels.get().primary && {
                clients: {
                    key: 'clients',
                    title: 'Clients',
                    icon: 'user-navigation-clients',
                    panels: [{
                        key: 'clients',
                        Component: Clients
                    },{
                        key: 'event_types_list',
                        Component: EventTypesList
                    },{
                        key: 'status_codes_list',
                        Component: StatusCodesList
                    }]
                }
            },
            ...user.level === User.levels.get().primary && {
                client_profile: {
                    key: 'client_profile',
                    title: 'My Client',
                    icon: 'user-navigation-clients'
                }
            },
            connect: {
                icon: 'user-navigation-connect',
                key: 'connect',
                title: 'Connect',
                permissions: ['connect']
            },
            leads: {
                key: 'leads',
                title: 'Leads',
                icon: 'user-navigation-leads',
                subViews: {
                    new: {
                        key: 'new',
                        permissions: ['leads.actions.new'],
                        title: 'Add New'
                    },
                    browse: {
                        key: 'browse',
                        title: 'Browse',
                        panels: [{
                            key: 'leads',
                            permissions: ['leads.list.general'],
                            Component: Leads.bind(this, {
                                id: 'leads',
                                name: 'Leads',
                                props: {
                                    cache_key: 'general',
                                    manage_duplicates: true,
                                    smart_share: true
                                }
                            })
                        },{
                            key: 'new_leads',
                            permissions: ['leads.list.new'],
                            Component: Leads.bind(this, {
                                id: 'new_leads',
                                name: 'New Leads',
                                filter_props: {
                                    status_codes: [Lead.status.get().new]
                                },
                                props: {
                                    cache_key: 'new',
                                    smart_share: true
                                }
                            })
                        },{
                            key: 'assigned_leads',
                            permissions: ['leads.list.assigned'],
                            Component: Leads.bind(this, {
                                id: 'assigned_leads',
                                name: 'Assigned Leads',
                                props: {
                                    cache_key: 'assigned',
                                    assignments: true
                                }
                            })
                        },{
                            key: 'follow_up_leads',
                            permissions: ['leads.list.follow_up'],
                            Component: Leads.bind(this, {
                                id: 'follow_up_leads',
                                name: 'Follow Up Leads',
                                props: {
                                    cache_key: 'follow_ups',
                                    follow_ups: true
                                }
                            })
                        },{
                            key: 'unreleased_leads',
                            permissions: ['leads.list.unreleased'],
                            visible: user.level <= User.levels.get().primary,
                            Component: Leads.bind(this, {
                                id: 'unreleased_leads',
                                name: 'Unreleased Leads',
                                props: {
                                    cache_key: 'unreleased',
                                    released: false
                                }
                            })
                        },{
                            key: 'transfer_leads',
                            permissions: ['leads.list.transfers'],
                            visible: user.level <= User.levels.get().primary,
                            Component: Leads.bind(this, {
                                id: 'transfer_leads',
                                name: 'Lead Transfers',
                                props: {
                                    cache_key: 'transfers',
                                    transfers: true
                                }
                            })
                        },{
                            key: 'archived_leads',
                            permissions: ['leads.list.archived'],
                            Component: Leads.bind(this, {
                                id: 'archived_leads',
                                name: 'Archived Leads',
                                props: {
                                    cache_key: 'archived',
                                    show_archived_only: true
                                }
                            })
                        },{
                            key: 'do_not_call_leads',
                            permissions: ['leads.list.do_not_call'],
                            Component: Leads.bind(this, {
                                id: 'do_not_call_leads',
                                name: 'Do Not Call Leads',
                                filter_props: {
                                    status_codes: [ Lead.status.get().do_not_call ]
                                },
                                props: {
                                    cache_key: 'do_not_call'
                                }
                            })
                        },{
                            key: 'out_of_service_area_leads',
                            permissions: ['leads.list.out_of_service_area'],
                            Component: Leads.bind(this, {
                                id: 'out_of_service_area_leads',
                                name: 'Out of Service Area Leads',
                                props: {
                                    cache_key: 'out_of_service_area',
                                    out_of_service_area: true,
                                    smart_share: true
                                }
                            })
                        }]
                    },
                    imports: {
                        key: 'imports',
                        level: User.levels.get().primary,
                        title: 'Imports',
                        panels: [{
                            key: 'lead_import_history',
                            permissions: ['leads.list.import_history'],
                            Component: LeadImportHistory
                        }]
                    },
                    outbound_calls: {
                        key: 'outbound_calls',
                        title: 'Outbound Calls',
                        panels: [{
                            key: 'outbound_calls_calendar',
                            title: 'Call and Email Assignments',
                            permissions: ['calls.calendar'],
                            Component: OutboundCallsCalendar
                        },{
                            key: 'outbound_call_logs',
                            permissions: ['calls.list.all'],
                            Component: OutboundCallLogs
                        }]
                    },
                    resources: {
                        key: 'resources',
                        level: User.levels.get().primary,
                        title: 'Resources',
                        panels: [{
                            key: 'lead_types_list',
                            permissions: ['leads.list.types'],
                            Component: LeadTypesList
                        },{
                            key: 'lead_sub_types_list',
                            permissions: ['leads.list.sub_types'],
                            Component: LeadSubTypesList
                        },{
                            key: 'lead_scripts_list',
                            permissions: ['leads.list.scripts'],
                            Component: LeadScriptsList
                        }]
                    },
                    unassign: {
                        key: 'unassign_leads',
                        level: User.levels.get().primary,
                        title: 'Unassign',
                        permissions: ['leads.actions.unassign']
                    },
                }
            },
            locations: {
                key: 'locations',
                title: 'Locations',
                icon: 'user-navigation-locations',
                permissions: ['leads.list.locations'],
                panels: [{
                    key: 'client_locations_map',
                    visible: user.level <= User.levels.get().primary,
                    Component: ClientLocationsMap
                },{
                    key: 'lead_locations',
                    Component: LeadLocations
                }]
            },
            surveys: {
                icon: 'user-navigation-surveys',
                key: 'surveys',
                title: 'Surveys',
                subViews: {
                    browse: {
                        key: 'browse',
                        title: 'Browse',
                        level: User.levels.get().primary,
                        panels: [{
                            key: 'surveys',
                            permissions: ['surveys.list.surveys'],
                            Component: Surveys.bind(this, {
                                id: 'surveys',
                                title: 'Survey'
                            })
                        }]
                    },
                    survey_monkey: {
                        key: 'survey_monkey',
                        level: User.levels.get().primary,
                        title: 'Survey Monkey',
                        permissions: ['surveys.survey_monkey']
                    },
                    templates: {
                        key: 'templates',
                        title: 'Templates',
                        level: User.levels.get().primary,
                        permissions: ['surveys.list.templates'],
                        panels: [{
                            key: 'survey_templates',
                            Component: SurveyTemplates
                        }]
                    }
                }
            },
            users: {
                key: 'users',
                title: 'Users',
                icon: 'user-navigation-users',
                subViews: {
                    accounts: {
                        key: 'accounts',
                        title: 'Accounts',
                        panels: levels.filter(entry => {
                            return entry.level >= utils.user.get().level;
                        }).map(entry => {
                            return {
                                key: `${entry.key}_users`,
                                permissions: entry.permissions,
                                Component: Users.bind(this, {
                                    level: entry.level,
                                    title: entry.title,
                                    shortName: entry.shortName,
                                    panelID: `${entry.key}_users`
                                })
                            }
                        })
                    },
                    availability: {
                        key: 'availability',
                        level: User.levels.get().setter,
                        title: 'Availability',
                        panels: [{
                            key: 'user_availability',
                            Component: UserAvailabilityPanel
                        }]
                    },
                    notifications: {
                        key: 'notifications',
                        title: 'Notifications',
                        permissions: ['users.list.notifications'],
                        panels: [{
                            key: 'notifications',
                            Component: Notifications
                        }]
                    },
                    system_events: {
                        key: 'system_events',
                        title: 'System Events',
                        permissions: ['users.list.system_events'],
                        panels: [{
                            key: 'system_events',
                            Component: SystemEvents
                        }]
                    }
                }
            },
            ...user.level <= User.levels.get().primary && {
                settings: {
                    key: 'settings',
                    title: 'Settings',
                    icon: 'user-navigation-settings',
                    permissions: ['settings']
                }
            }
        }

    } catch(e) {
        return {};
    }
}
